
import { switchMap, tap, catchError, mergeMap, debounceTime, takeUntil, delay } from 'rxjs/operators'
import orderBy from 'lodash/orderBy'
import {
  GetFutureApiResponse,
  NetworthBreakdownDate,
  FutureCategoryBreakDown,
  FutureLayerSlug,
  FutureLayerUID,
  FutureLayer,
  FutureLayerFields,
  FutureLayerBase,
  FutureAssumptionsTaxHints,
  FutureAssumptions,
  FutureLayerDefinedHints,
  FutureWhen,
} from 'components/features/future/types'
import { normalize } from 'helpers/normalizeHelpers'
import { DebtAssetCategoryId } from 'interfaces/meta'
import { defaultMemoize } from 'reselect'
import { getOldCategory } from 'helpers/categoriesTranslateHelper'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { layerSelector } from 'components/features/future/selectors'
import { ObjectSchema } from 'yup'
import { createCommonSchema } from 'schemas/futureSchema'
import { useFormContext } from 'react-hook-form'
import { Omit } from 'interfaces'
import { Timeframe } from 'interfaces/chart'
import moment from 'moment'
import { isAssetsCategory } from 'helpers/categoriesHelpers'
import { useEffect, useRef, useMemo } from 'react'
import debounce from 'lodash/debounce'
import { DropLayerCheckAction, getLayerHintsActionCreator, layerCheckActionCreator } from 'components/features/future/actions'
import isEqual from 'lodash/isEqual'
import { Subject, of, EMPTY } from 'rxjs'
import { ASSUMPTIONS_HINTS_URL, LAYER_HINTS_URL } from 'interfaces/ajax'
import { post } from 'helpers/observableAjaxHelpers'
import { createGetAgeMonthPrecision } from 'helpers/dateTimeHelpers'

export const normalizeFutureResponse = (response: GetFutureApiResponse) => {
  const { layers, networth_breakdown, networth_forecast } = response

  const series = networth_breakdown.series.map(({ date, values }) => ({
    date,
    values: values.map(({ amount, category }) => ({
      amount,
      category: getOldCategory(category),
    })),
  }))

  const layersWithDate = layers.map((layer) => ({
    ...layer,
    sortGroup: +!!layer.when_month,
    sortDate: !layer.when_month ? layer.created : layer.when_month,
  }))
  const layersSorted: FutureLayer[] = orderBy(layersWithDate, ['sortGroup', 'sortDate'], ['asc', 'asc'])

  return {
    networth_breakdown: { series },
    networth_forecast,
    layers: normalize(layersSorted, 'uid'),
  }
}

export type FutureNormalizedApiResponse = ReturnType<typeof normalizeFutureResponse>


export const splitAndSortBreakdownDate = defaultMemoize((item: NetworthBreakdownDate) => {
  const { assets: localAssets, debts: localDebts } = item.values.reduce((acc, value) => {
    const isAsset = isAssetsCategory(value.category)

    return {
      assets: [
        ...acc.assets,
        ...isAsset ? [value] : [],
      ],
      debts: [
        ...acc.debts,
        ...!isAsset ? [value] : [],
      ],
    }
  }, {
    assets: [] as FutureCategoryBreakDown[],
    debts: [] as FutureCategoryBreakDown[],
  })


  return {
    assets: orderBy(localAssets, ['amount'], ['desc']),
    debts: orderBy(localDebts, ['amount'], ['desc']),
  }
})

const getBreakdownDateBounds = (item: NetworthBreakdownDate) => {
  return item.values.reduce(({ positive, negative }, category) => {
    const sign = isAssetsCategory(category.category) ? 1 : -1
    const amount = category.amount * sign

    if (amount > 0) {
      positive += amount
    } else {
      negative += amount
    }

    return { positive, negative }
  }, {
    positive: 0,
    negative: 0,
  })
}

export const getBreakdownBounds = defaultMemoize((items: NetworthBreakdownDate[]) => {
  return items.reduce(({ positive, negative }, item) => {
    const dateBounds = getBreakdownDateBounds(item)
    return {
      positive: Math.max(positive, dateBounds.positive),
      negative: Math.min(negative, dateBounds.negative),
    }
  }, {
    positive: 0,
    negative: 0,
  })
})


type CategoryGroupKey = 'debts' | 'assets'

const getCategoryInfo = (item: FutureCategoryBreakDown, index: number, type: CategoryGroupKey) => ({
  type,
  index,
  amount: item.amount,
})
type CategoryInfoType = ReturnType<typeof getCategoryInfo>

export const getCategoriesMap = defaultMemoize((items: FutureCategoryBreakDown[], type: CategoryGroupKey) => {
  return items.reduce((acc, item, index) => ({
    ...acc,
    [item.category]: getCategoryInfo(item, index, type),
  }), {} as Record<DebtAssetCategoryId, CategoryInfoType>)
})

export const useLayerSelector = <Slug extends FutureLayerSlug>(props: { uid?: FutureLayerUID }) => {
  return useStoreSelector((state) => layerSelector(state, props)) as Extract<FutureLayer, { slug: Slug }> | null
}

// sorry for this. it seems for me there is not way to extends schema without typescript static issues
type LayerBaseFields = keyof FutureLayerBase<{ slug: 'retirement' }>
type OmitLayerFields = Pick<FutureLayerFields, LayerBaseFields>
export const extendCommonSchema =
  <
    Slug extends FutureLayerFields['slug'],
    LayerType extends OmitLayerFields = Extract<FutureLayerFields, { slug: Slug }>,
    LayerTypeFull extends OmitLayerFields = Extract<FutureLayer, { slug: Slug }>
  >
  (schema: ObjectSchema<Omit<LayerType, LayerBaseFields>>) => (birthDate: string): ObjectSchema<LayerTypeFull> => {
    const commonSchema = createCommonSchema(birthDate) as any as ObjectSchema<LayerType>
    return commonSchema.concat(schema as ObjectSchema<LayerType>) as any
  }

const timeframeToYearsMap: Partial<Record<Timeframe, number>> = {
  'next-10-years': 10,
  'next-20-years': 20,
  'next-30-years': 30,
  future: 60,
}

export const timeframeToDateRange = (timeframe: Timeframe, birthDate: string, lastDate: string) => {
  const birthDateMoment = moment(birthDate).startOf('month')
  const age = moment().diff(birthDateMoment, 'years')
  const fromDate = moment(birthDateMoment).add(age, 'years')
  const defaultToDate = moment(lastDate).valueOf()

  const years = timeframeToYearsMap[timeframe] || null
  if (!years) {
    return {
      fromDate: fromDate.valueOf(),
      toDate: defaultToDate,
    }
  }

  return {
    fromDate: fromDate.valueOf(),
    toDate: Math.min(fromDate.add(years, 'years').valueOf(), defaultToDate),
  }

}
type DateRange = ReturnType<typeof timeframeToDateRange>

export const isDateInRange = (dateString: string, { fromDate, toDate }: DateRange) => {
  const date = moment(dateString).valueOf()
  return fromDate <= date && toDate >= date
}

export const filterTimeframe = <T extends { date: string }>(series: T[], timeframe: Timeframe, birthDate: string, lastDate: string): T[] => {
  const dateRange = timeframeToDateRange(timeframe, birthDate, lastDate)
  return series.filter((item) => isDateInRange(item.date, dateRange))
}

export const cleanUpObj = <T = any>(obj: any): Partial<T> => {
  const { is_linked, ...restObj } = obj
  const keys = Object.keys(restObj) as (keyof T)[]
  const result = keys.reduce((acc, key) => ({
    ...acc,
    ...(!(key in restObj) || restObj[key] === null || restObj[key] === '' || restObj[key] === undefined) ? {} : { [key]: restObj[key] },
  }), {} as Partial<T>)

  return result
}

/**
 * In fact, only the buy_home layer is supported. Hook needs to be rewritten to the BuyHomeLayerCheckAction with his own epic
 *
 */
export const useLayerCheck = (uid?: FutureLayerUID) => {
  const { getValues, watch } = useFormContext<FutureLayer>()
  const values = watch()
  const prevValuesRef = useRef(values)

  const hasChanges = !isEqual(values, prevValuesRef.current)
  prevValuesRef.current = values

  const forceUpdateRef = useRef(0)
  if (hasChanges) {
    forceUpdateRef.current++
    prevValuesRef.current = values
  }
  const dispatch = useStoreDispatch()


  const handleChange = useMemo(() => debounce(
    () => {
      const prevValues = getValues()
      const data = {
        ...prevValues,
        uid,
      } as FutureLayer
      const props = cleanUpObj(data)

      if (prevValues.slug === 'buy_home' && +prevValues.purchase_price === 0) {
        dispatch(new DropLayerCheckAction())
        return
      }

      dispatch(layerCheckActionCreator.request(props))
    }, 300
  ), [dispatch, uid, getValues])

  useEffect(handleChange, [handleChange])

  useEffect(() => () => {
    handleChange.cancel()
  }, [handleChange])
}

/**
 * If bugs occur, please check how useHints works,
 * using 'deps' and 'requiredFields' parameters
 *
 */
export const useSellAssetHints = (uid?: FutureLayerUID) => {
  const { getValues, watch } = useFormContext<FutureLayer>()
  const slug = watch('slug') as FutureLayerSlug | undefined
  const whenSlug = watch('when.slug') as FutureWhen['slug'] | undefined
  const whenYears = watch('when.years') as unknown
  const whenAge = watch('when.age') as unknown
  const whenMonth = watch('when.month') as unknown
  const assetUid = watch('asset_uid') as unknown

  const dispatch = useStoreDispatch()

  const handleChange = useMemo(() => debounce(
    () => {
      const prevValues = getValues()
      const data = {
        ...prevValues,
        uid,
      } as FutureLayer
      const props = cleanUpObj(data)

      dispatch(getLayerHintsActionCreator.request({ ...props, slug: prevValues.slug }))
    }, 300
  ), [dispatch, uid, getValues])

  useEffect(() => {
    if (!slug || !whenSlug) {
      return
    }

    if (whenSlug === 'at_month' && !whenMonth) {
      return
    }
    if (whenSlug === 'in_certain_years' && whenYears === undefined) {
      return
    }
    if (whenSlug === 'at_age' && whenAge === undefined) {
      return
    }

    handleChange()
  }, [handleChange, slug, whenSlug, whenYears, whenAge, whenMonth, assetUid])

  useEffect(() => () => {
    handleChange.cancel()
  }, [handleChange])
}

/**
 * Pass `['slug']` as `deps` param if you don't want to track form values changes
 * TODO: Should be rewritten with epic, according to useLayerCheck
 *
 */
export const useHints =
  <
    Slug extends FutureLayerSlug,
    LayerHints = Extract<FutureLayerDefinedHints, { slug: Slug }>['hints'],
    LayerType = Extract<FutureLayer, { slug: Slug }>,
  >
  (
    handler: (hints: LayerHints) => void,
    deps: (keyof LayerType)[],
    requiredFields?: (keyof LayerType)[]
  ) => {
    const { getValues, watch } = useFormContext<FutureLayer>()
    const getValuesRef = useRef(getValues)
    getValuesRef.current = getValues

    const slug = getValues('slug') as Slug

    const handlerRef = useRef(handler)
    handlerRef.current = handler

    const requestSubject = useMemo(() => new Subject(), [])

    useEffect(() => {
      if (!slug) {
        return
      }

      const destroySubject = new Subject()

      requestSubject.pipe(
        mergeMap(() => {
          const currentValues = getValuesRef.current()
          if (currentValues.slug === 'buy_home' && +currentValues.purchase_price === 0) {
            return EMPTY
          }
          return of(currentValues)
        }),
        switchMap((currentValues) => of(null).pipe(
          delay(500),
          mergeMap(() => post(
            { url: LAYER_HINTS_URL, params: { slug }}, { body: cleanUpObj(currentValues) }
          ).pipe(
            takeUntil(destroySubject),
            tap(({ response }) => handlerRef.current(response as any)),
            catchError(() => EMPTY),
          )),
        )),
        takeUntil(destroySubject),
      ).subscribe()

      return () => {
        destroySubject.next()
      }
    }, [slug, requestSubject])

    const values = watch() as any as Record<keyof LayerType, string>

    const depsValues = useMemo(() => {
      return deps.map((prop) => values[prop])
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, ...deps])

    const requiredList = requiredFields || deps
    const hasEmptyRequired = useMemo(() => {
      return requiredList.some((prop) => values[prop] === undefined)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, ...depsValues])

    useEffect(() => {
      if (!slug || hasEmptyRequired) {
        return
      }

      requestSubject.next()
    // keep `slug` here to have correct sequence of effects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, requestSubject, ...depsValues])
  }

export const useAssumptionsTaxHints = (handler: (hints: FutureAssumptionsTaxHints) => void) => {
  const { getValues, watch } = useFormContext<FutureAssumptions>()
  const getValuesRef = useRef(getValues)
  getValuesRef.current = getValues


  const handlerRef = useRef(handler)
  handlerRef.current = handler

  const requestSubject = useMemo(() => new Subject(), [])

  useEffect(() => {

    const destroySubject = new Subject()
    requestSubject.pipe(
      switchMap(() => of(null)),
      debounceTime(500),
      mergeMap(() => post(
        ASSUMPTIONS_HINTS_URL,
        { body: cleanUpObj(getValuesRef.current(['annual_salary'] as any)) }
      ).pipe(
        tap(({ response }) => handlerRef.current(response)),
        catchError(() => EMPTY),
      )),
      takeUntil(destroySubject),
    ).subscribe()

    return () => {
      destroySubject.next()
    }
  }, [requestSubject])

  const annual_salary = watch('annual_salary')

  useEffect(() => {
    if (annual_salary === undefined) {
      return
    }

    requestSubject.next()
  }, [requestSubject, annual_salary])
}

export const findRetireIndex = (items: { date: string }[], birthDate: string, retireAge: number) => {
  const getAge = createGetAgeMonthPrecision(birthDate)
  return items.findIndex((item) => {
    return getAge(item.date) >= retireAge
  })
}
